/**
 * Native navigation map overlay service.
 * Controls a native MapLibre MapView that sits on top of the WebView
 * during active route following. Provides 60fps smooth map rendering
 * with heading tracking, route polyline, and tilt perspective.
 *
 * Only active on Android native — web and iOS fall back to Leaflet.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

interface NativeNavMapPlugin {
  show(options: {
    style: string;
    latitude: number;
    longitude: number;
    zoom: number;
    bearing: number;
  }): Promise<void>;
  hide(): Promise<void>;
  updatePosition(options: {
    latitude: number;
    longitude: number;
    bearing: number;
    zoom: number;
    animate: boolean;
  }): Promise<void>;
  setRoute(options: { coordinates: [number, number][] }): Promise<void>;
  clearRoute(): Promise<void>;
  setStyle(options: { style: string }): Promise<void>;
  setNextSpot(options: { latitude?: number; longitude?: number; name?: string }): Promise<void>;
  updateInfoOverlay(options: { text: string }): Promise<void>;
  destroy(): Promise<void>;
}

const NativeNavMap = Capacitor.getPlatform() === 'android'
  ? registerPlugin<NativeNavMapPlugin>('NativeNavMap')
  : null;

/**
 * Check if native nav map is available on this platform.
 */
export function isNativeNavMapAvailable(): boolean {
  return NativeNavMap !== null;
}

/**
 * Show the native map overlay for navigation mode.
 */
export async function showNativeNavMap(options: {
  style: 'topo' | 'satellite';
  latitude: number;
  longitude: number;
  zoom?: number;
  bearing?: number;
}): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.show({
      style: options.style,
      latitude: options.latitude,
      longitude: options.longitude,
      zoom: options.zoom ?? 15,
      bearing: options.bearing ?? 0,
    });
  } catch (e) {
    console.warn('[NativeNavMap] Failed to show:', e);
  }
}

/**
 * Hide the native map overlay (returns to WebView map).
 */
export async function hideNativeNavMap(): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.hide();
  } catch (e) {
    console.warn('[NativeNavMap] Failed to hide:', e);
  }
}

/**
 * Update camera position during navigation (smooth 60fps animation).
 */
export async function updateNativeNavPosition(
  latitude: number,
  longitude: number,
  bearing: number,
  zoom = 15,
  animate = true,
): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.updatePosition({ latitude, longitude, bearing, zoom, animate });
  } catch (e) {
    // Throttled — don't warn on every frame
  }
}

/**
 * Set the route polyline on the native map.
 * Coordinates in GeoJSON format: [lon, lat][].
 */
export async function setNativeNavRoute(coordinates: [number, number][]): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.setRoute({ coordinates });
  } catch (e) {
    console.warn('[NativeNavMap] Failed to set route:', e);
  }
}

/**
 * Clear route polyline from native map.
 */
export async function clearNativeNavRoute(): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.clearRoute();
  } catch (e) {
    console.warn('[NativeNavMap] Failed to clear route:', e);
  }
}

/**
 * Switch map style (topo/satellite) on native map.
 */
export async function setNativeNavStyle(style: 'topo' | 'satellite'): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.setStyle({ style });
  } catch (e) {
    console.warn('[NativeNavMap] Failed to set style:', e);
  }
}

/**
 * Set/update the "next Fricamp along route" marker on the native map.
 * Pass null/undefined values to clear it.
 */
export async function setNativeNavNextSpot(
  spot: { latitude: number; longitude: number; name?: string } | null,
): Promise<void> {
  if (!NativeNavMap) return;
  try {
    if (spot) {
      await NativeNavMap.setNextSpot({
        latitude: spot.latitude,
        longitude: spot.longitude,
        name: spot.name ?? '',
      });
    } else {
      await NativeNavMap.setNextSpot({});
    }
  } catch (e) {
    console.warn('[NativeNavMap] Failed to set next spot:', e);
  }
}

/**
 * Update the top-center info overlay (remaining distance + duration + next maneuver).
 * Pass empty string to hide.
 */
export async function setNativeNavInfoText(text: string): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.updateInfoOverlay({ text });
  } catch (e) {
    console.warn('[NativeNavMap] Failed to update info overlay:', e);
  }
}

/**
 * Destroy the native map overlay and free resources.
 */
export async function destroyNativeNavMap(): Promise<void> {
  if (!NativeNavMap) return;
  try {
    await NativeNavMap.destroy();
  } catch (e) {
    console.warn('[NativeNavMap] Failed to destroy:', e);
  }
}
